import type { PropsWithChildren } from 'react'
import { route } from 'routes-gen'

import { json, redirect } from '@remix-run/node'
import type { LoaderFunctionArgs } from '@remix-run/node'
import type { ShouldRevalidateFunction } from '@remix-run/react'
import { Outlet, useLoaderData } from '@remix-run/react'
import { useCaptureRouteError } from '~/utils/sentry-route-error'

import { CircularStep, FullPageLayout, Steps } from '~/components'
import { ErrorPage } from '~/components/convenience/ErrorPage'
import { UsersIcon, ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/20/solid'
import { i18n } from '@lingui/core'

const STEPS = ['anmeldung', 'identifikation', 'passwort']

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  defaultShouldRevalidate,
  nextUrl,
}) => {
  if (!defaultShouldRevalidate) {
    return currentUrl !== nextUrl
  }
  return true
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const step = STEPS.find((step) => url.pathname.includes(`/${step}`))
  if (url.pathname == '/erstanmeldung/schritt' || url.pathname == '/erstanmeldung/schritt/') {
    return redirect(route('/erstanmeldung/schritt/anmeldung'))
  }

  return json({ step })
}

const DocumentLayout = ({ step, children }: PropsWithChildren<{ step?: string }>) => {
  return (
    <FullPageLayout>
      <header className="w-full">
        <div className="py-8 md:py-10">
          <Steps className="justify-center items-center">
            <CircularStep
              title={i18n._('Anmeldung')}
              description={i18n._('Schritt 1')}
              icon={UsersIcon}
              status={step == 'anmeldung' ? 'current' : 'complete'}
              isFirstStep
            />
            <CircularStep
              title={i18n._('Identifikation')}
              description={i18n._('Schritt 2')}
              icon={ShieldCheckIcon}
              status={
                step == 'anmeldung' ? 'complete' : step == 'identifikation' ? 'current' : 'upcoming'
              }
            />
            <CircularStep
              title={i18n._('Passwortvergabe')}
              description={i18n._('Schritt 3')}
              icon={LockClosedIcon}
              isLastStep
              status={step == 'passwort' ? 'current' : 'upcoming'}
            />
          </Steps>
        </div>
      </header>

      {children}
    </FullPageLayout>
  )
}

export function RegisterStepsRoute() {
  const { step } = useLoaderData<typeof loader>()
  return (
    <DocumentLayout step={step}>
      <Outlet />
    </DocumentLayout>
  )
}

export default RegisterStepsRoute

export function ErrorBoundary() {
  useCaptureRouteError()
  return (
    <DocumentLayout>
      <div className="h-screen flex flex-col justify-center">
        <ErrorPage />
      </div>
    </DocumentLayout>
  )
}
